import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

export type KnownTrailDifficulty = 'easy' | 'moderate' | 'hard' | 'strenuous';

const trailDifficultyLabels: Record<KnownTrailDifficulty, MessageDescriptor> = defineMessages({
  easy: {
    defaultMessage: 'Easy'
  },
  moderate: {
    id: 'difficulty.Moderate',
    defaultMessage: 'Moderate'
  },
  hard: {
    defaultMessage: 'Hard'
  },
  strenuous: {
    defaultMessage: 'Strenuous'
  }
});

export const getTrailDifficultyLabel = (intl: IntlShape, difficulty: KnownTrailDifficulty) => {
  return intl.formatMessage(trailDifficultyLabels[difficulty]);
};

const trailDifficultyDescriptions: Record<KnownTrailDifficulty, MessageDescriptor> = defineMessages({
  easy: {
    defaultMessage: 'Easy trails are typically short, with clearly marked paths and fairly level terrain.'
  },
  moderate: {
    defaultMessage: 'Moderate trails often feature gradual climbs or obstacles like rocks or roots.'
  },
  hard: {
    defaultMessage: 'Hard trails usually cover longer distances, and may have long or steep climbs or difficult obstacles.'
  },
  strenuous: {
    defaultMessage: 'Strenuous trails are for advanced adventurers who are prepared for physically demanding routes that may have technical terrain.'
  }
});

export const getTrailDifficultyDescription = (intl: IntlShape, difficulty: KnownTrailDifficulty) => {
  return intl.formatMessage(trailDifficultyDescriptions[difficulty]);
};

export const getTrailDifficultyFromNumber = (difficulty: number): KnownTrailDifficulty => {
  switch (difficulty) {
    case 1:
    case 2:
      return 'easy';
    case 3:
    case 4:
      return 'moderate';
    case 5:
    case 6:
      return 'hard';
    case 7:
    case 8:
      return 'strenuous';
    default:
      return 'moderate';
  }
};

// difficulty is optional to preserve backwards compatibility with existing code
export const getTrailDifficultyLabelFromNumber = (intl: IntlShape, difficulty?: number) => {
  if (!difficulty) {
    return undefined;
  }
  if (difficulty < 1 || difficulty > 8) {
    return undefined;
  }
  return getTrailDifficultyLabel(intl, getTrailDifficultyFromNumber(difficulty));
};
